import { useEffect, useState } from "react";
import { api, formatApiError } from "./api";
import { emptyRule, FIELD_OPTIONS, MATCH_MODES, RULE_TYPES, VERDICTS } from "./rule-constants";

/**
 * Modal editor for one rule. `initial` can be:
 *   - {}            -> create new
 *   - {id, ...}     -> edit existing
 */
export default function RuleEditor({ initial, onClose, onSaved }) {
  const isNew = !initial?.id;
  const [form, setForm] = useState(() => ({ ...emptyRule(), ...(initial || {}) }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("edit");   // "edit" | "history"
  const [versions, setVersions] = useState([]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    setForm({ ...emptyRule(), ...(initial || {}) });
    setTab("edit");
    setErr("");
  }, [initial]);

  const loadHistory = async () => {
    if (isNew) return;
    try {
      const { data } = await api.get(`/rules/${initial.id}/versions`);
      setVersions(data);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  useEffect(() => {
    if (tab === "history") loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const save = async () => {
    // Drop empty alternatives so the rule isn't disabled accidentally.
    const payload = {
      ...form,
      when_alternatives: (form.when_alternatives || [])
        .map((s) => (s || "").trim()).filter(Boolean),
    };
    setBusy(true); setErr("");
    try {
      if (isNew) {
        await api.post("/rules", payload);
      } else {
        await api.put(`/rules/${initial.id}`, payload);
      }
      onSaved && onSaved();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const restore = async (version) => {
    if (!window.confirm(
      `Restore version saved at ${version.saved_at?.slice(0, 16).replace("T", " ")} by ${version.saved_by}?\n\nThe current values will become a new version in the history.`)) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/rules/${initial.id}/restore/${version.id}`);
      onSaved && onSaved();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={S.backdrop} onClick={onClose}>
      <div style={S.panel} onClick={(e) => e.stopPropagation()} data-testid="admin-rule-editor">
        <div style={S.head}>
          <div>
            <div style={S.headTitle}>
              {isNew ? "New rule" : `Edit · ${initial.id}`}
            </div>
            <div style={S.headSub}>
              Saving auto-reloads Part B. Version history is per-rule.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              data-testid="admin-editor-tab-edit"
              onClick={() => setTab("edit")}
              style={{ ...S.tab, ...(tab === "edit" ? S.tabActive : {}) }}
            >
              Edit
            </button>
            <button
              data-testid="admin-editor-tab-history"
              disabled={isNew}
              onClick={() => setTab("history")}
              style={{
                ...S.tab,
                ...(tab === "history" ? S.tabActive : {}),
                opacity: isNew ? 0.4 : 1,
                cursor: isNew ? "not-allowed" : "pointer",
              }}
            >
              History
            </button>
            <button data-testid="admin-editor-close" onClick={onClose} style={S.close}>×</button>
          </div>
        </div>

        {err && <div style={S.err}>{err}</div>}

        {tab === "edit" ? (
          <div style={S.body}>
            <Row label="Rule type">
              <select
                data-testid="admin-editor-type"
                value={form.type}
                onChange={(e) => set("type", e.target.value)}
                style={S.input}
              >
                {RULE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </Row>

            <Row label="Active">
              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  data-testid="admin-editor-active"
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => set("active", e.target.checked)}
                  style={{ accentColor: "#10b981" }}
                />
                <span style={{ color: "#94a3b8", fontSize: 12 }}>
                  Inactive rules are kept but ignored by Part B.
                </span>
              </label>
            </Row>

            <Row label="Title (for the rules list)">
              <input
                data-testid="admin-editor-title"
                value={form.title}
                onChange={(e) => set("title", e.target.value)}
                placeholder="One-line summary"
                style={S.input}
              />
            </Row>

            <Row label="Description / senior notes">
              <textarea
                data-testid="admin-editor-description"
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                rows={2}
                placeholder="Why this rule exists, edge cases, history…"
                style={{ ...S.input, fontFamily: "inherit", resize: "vertical" }}
              />
            </Row>

            {/* --- DETECT fields --- */}
            {form.type === "DETECT" && (
              <>
                <hr style={S.hr} />
                <SectionLabel>IF — when to trigger</SectionLabel>

                <Row label="Field to check (in the Communication Excel row)">
                  <input
                    data-testid="admin-editor-when-field"
                    list="field-options"
                    value={form.when_field}
                    onChange={(e) => set("when_field", e.target.value)}
                    placeholder="e.g. procedure_done"
                    style={S.input}
                  />
                  <datalist id="field-options">
                    {FIELD_OPTIONS.map((f) => <option key={f} value={f} />)}
                  </datalist>
                </Row>

                <Row label="ANY of these patterns matches (regex, case-insensitive)">
                  <AlternativesEditor
                    values={form.when_alternatives}
                    onChange={(v) => set("when_alternatives", v)}
                  />
                </Row>

                <Row label="Also requires (optional)  — format: another_field:regex">
                  <input
                    data-testid="admin-editor-also-requires"
                    value={form.also_requires}
                    onChange={(e) => set("also_requires", e.target.value)}
                    placeholder='e.g. claim_type_code:^CO$'
                    style={S.input}
                  />
                </Row>

                <hr style={S.hr} />
                <SectionLabel>THEN — what verdict to apply</SectionLabel>

                <Row label="Verdict">
                  <select
                    data-testid="admin-editor-verdict"
                    value={form.verdict}
                    onChange={(e) => set("verdict", e.target.value)}
                    style={S.input}
                  >
                    {VERDICTS.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </Row>

                <Row label="Irregularity site">
                  <input
                    data-testid="admin-editor-site"
                    value={form.irregularity_site}
                    onChange={(e) => set("irregularity_site", e.target.value)}
                    placeholder="e.g. Examination; Procedure Parameters"
                    style={S.input}
                  />
                </Row>

                <Row label="Irregularity description (what's wrong)">
                  <textarea
                    data-testid="admin-editor-irregularity-desc"
                    value={form.irregularity_description}
                    onChange={(e) => set("irregularity_description", e.target.value)}
                    rows={3}
                    style={{ ...S.input, fontFamily: "inherit", resize: "vertical" }}
                  />
                </Row>

                <Row label="Corrective action (what should be added/changed)">
                  <textarea
                    data-testid="admin-editor-corrective-action"
                    value={form.corrective_action}
                    onChange={(e) => set("corrective_action", e.target.value)}
                    rows={4}
                    style={{ ...S.input, fontFamily: "inherit", resize: "vertical" }}
                  />
                </Row>

                <Row label="Guideline reference">
                  <input
                    data-testid="admin-editor-guideline-ref"
                    value={form.guideline_reference}
                    onChange={(e) => set("guideline_reference", e.target.value)}
                    placeholder="e.g. DOH Aesthetic Procedures Standard §3.2"
                    style={S.input}
                  />
                </Row>
              </>
            )}

            {/* --- ACCEPT_IF / REJECT_IF --- */}
            {(form.type === "ACCEPT_IF" || form.type === "REJECT_IF") && (
              <>
                <hr style={S.hr} />
                <SectionLabel>
                  {form.type === "ACCEPT_IF" ? "Clinic policy (ACCEPT)" : "Clinic policy (REJECT)"}
                </SectionLabel>
                <Row label="Applies when procedure text contains (leave blank = all claims)">
                  <input
                    data-testid="admin-editor-applies-procedure"
                    value={form.applies_when_procedure_contains}
                    onChange={(e) => set("applies_when_procedure_contains", e.target.value)}
                    placeholder="e.g. laser hair removal"
                    style={S.input}
                  />
                </Row>
                <Row label="Policy statement (injected into the AI's system prompt)">
                  <textarea
                    data-testid="admin-editor-policy-statement"
                    value={form.policy_statement}
                    onChange={(e) => set("policy_statement", e.target.value)}
                    rows={5}
                    placeholder={
                      form.type === "ACCEPT_IF"
                        ? "e.g. For laser hair removal, GP clinicians ARE allowed even if Aesthetic Practitioner is normally required."
                        : "e.g. For laser hair removal, only CPT code 17999 is accepted; any other CPT must be flagged."
                    }
                    style={{ ...S.input, fontFamily: "inherit", resize: "vertical" }}
                  />
                </Row>
              </>
            )}

            {/* --- GOLD_MATCH --- */}
            {form.type === "GOLD_MATCH" && (
              <>
                <hr style={S.hr} />
                <SectionLabel>Gold-standard reference model</SectionLabel>
                <Row label="Applies when procedure text contains">
                  <input
                    data-testid="admin-editor-gold-procedure"
                    value={form.applies_when_procedure_contains}
                    onChange={(e) => set("applies_when_procedure_contains", e.target.value)}
                    placeholder="e.g. laser hair removal"
                    style={S.input}
                  />
                </Row>
                <Row label="Field to compare">
                  <input
                    data-testid="admin-editor-gold-field"
                    value={form.match_field}
                    onChange={(e) => set("match_field", e.target.value)}
                    placeholder="e.g. Chief Complaint"
                    style={S.input}
                  />
                </Row>
                <Row label="Expected reference value">
                  <textarea
                    data-testid="admin-editor-gold-expected"
                    value={form.expected_value}
                    onChange={(e) => set("expected_value", e.target.value)}
                    rows={4}
                    style={{ ...S.input, fontFamily: "inherit", resize: "vertical" }}
                  />
                </Row>
                <Row label="Match mode">
                  <select
                    data-testid="admin-editor-gold-mode"
                    value={form.match_mode}
                    onChange={(e) => set("match_mode", e.target.value)}
                    style={S.input}
                  >
                    {MATCH_MODES.map((m) => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                </Row>
                <Row label="Verdict if mismatched">
                  <select
                    data-testid="admin-editor-gold-severity"
                    value={form.severity_if_mismatch}
                    onChange={(e) => set("severity_if_mismatch", e.target.value)}
                    style={S.input}
                  >
                    {VERDICTS.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </Row>
              </>
            )}

            <hr style={S.hr} />
            <Row label="Extra notes (free text)">
              <textarea
                data-testid="admin-editor-notes"
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                rows={2}
                style={{ ...S.input, fontFamily: "inherit", resize: "vertical" }}
              />
            </Row>
          </div>
        ) : (
          <div style={S.body}>
            {versions.length === 0 ? (
              <div style={{ color: "#64748b", padding: 20 }}>
                No history yet. Every save creates a snapshot here.
              </div>
            ) : (
              versions.map((v) => (
                <div key={v.id} style={S.versionRow} data-testid={`admin-version-${v.id}`}>
                  <div>
                    <div style={{ fontSize: 13, color: "#e2e8f0" }}>
                      <b>{v.action.toUpperCase()}</b>
                      {" · "}{v.saved_at?.slice(0, 16).replace("T", " ")}
                    </div>
                    <div style={{ fontSize: 12, color: "#64748b" }}>by {v.saved_by}</div>
                  </div>
                  <button
                    data-testid={`admin-version-restore-${v.id}`}
                    onClick={() => restore(v)}
                    style={S.smallBtn}
                  >
                    Restore
                  </button>
                </div>
              ))
            )}
          </div>
        )}

        <div style={S.foot}>
          <div style={{ color: "#64748b", fontSize: 12 }}>
            {isNew ? "" : `Created by ${initial.created_by || "?"} on ${initial.created_at?.slice(0, 10) || "?"}`}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onClose} style={S.linkBtn}>Cancel</button>
            {tab === "edit" && (
              <button
                data-testid="admin-editor-save"
                onClick={save}
                disabled={busy}
                style={{ ...S.primary, opacity: busy ? 0.6 : 1 }}
              >
                {busy ? "Saving…" : isNew ? "Create rule" : "Save changes"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: 11, letterSpacing: 2, textTransform: "uppercase",
      color: "#22d3ee", marginBottom: 12, marginTop: 4,
    }}>
      {children}
    </div>
  );
}

function AlternativesEditor({ values, onChange }) {
  const safe = values && values.length ? values : [""];
  const update = (i, v) => {
    const arr = [...safe]; arr[i] = v; onChange(arr);
  };
  const add = () => onChange([...safe, ""]);
  const remove = (i) => onChange(safe.filter((_, j) => j !== i));
  return (
    <div data-testid="admin-editor-alts">
      {safe.map((v, i) => (
        <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6 }}>
          <input
            data-testid={`admin-editor-alt-${i}`}
            value={v}
            onChange={(e) => update(i, e.target.value)}
            placeholder="e.g.  laser\s+hair\s+removal"
            style={{ ...S.input, fontFamily: "ui-monospace, monospace", fontSize: 13 }}
          />
          <button
            data-testid={`admin-editor-alt-remove-${i}`}
            onClick={() => remove(i)}
            disabled={safe.length === 1}
            style={{ ...S.smallBtn, padding: "8px 12px", opacity: safe.length === 1 ? 0.3 : 1 }}
            title="Remove alternative"
          >
            ×
          </button>
        </div>
      ))}
      <button
        data-testid="admin-editor-alt-add"
        onClick={add}
        style={{ ...S.smallBtn, marginTop: 2 }}
      >
        + Add alternative
      </button>
    </div>
  );
}

const S = {
  backdrop: {
    position: "fixed", inset: 0, background: "rgba(2,6,23,0.7)",
    display: "flex", alignItems: "flex-start", justifyContent: "center",
    padding: "5vh 16px", zIndex: 100, overflowY: "auto",
  },
  panel: {
    background: "#0f172a", border: "1px solid #1f2a44", borderRadius: 12,
    width: "100%", maxWidth: 760, color: "#e2e8f0",
    display: "flex", flexDirection: "column", maxHeight: "90vh",
  },
  head: {
    display: "flex", justifyContent: "space-between",
    padding: "16px 20px", borderBottom: "1px solid #1f2a44",
  },
  headTitle: { fontSize: 16, fontWeight: 700, color: "#f1f5f9" },
  headSub:   { fontSize: 12, color: "#64748b", marginTop: 2 },
  tab: {
    background: "transparent", border: "1px solid #334155",
    color: "#cbd5e1", padding: "5px 12px", borderRadius: 6,
    fontSize: 12, cursor: "pointer",
  },
  tabActive: {
    background: "#1e293b", borderColor: "#334155", color: "#f1f5f9",
  },
  close: {
    background: "transparent", border: "none", color: "#94a3b8",
    fontSize: 24, cursor: "pointer", lineHeight: 1, padding: "0 4px",
  },
  body: { padding: 20, overflowY: "auto", flex: 1 },
  err: {
    background: "rgba(220,38,38,0.12)", border: "1px solid rgba(220,38,38,0.5)",
    color: "#fecaca", padding: "8px 16px", margin: "8px 20px 0",
    borderRadius: 8, fontSize: 13,
  },
  hr: { border: "none", borderTop: "1px solid #1f2a44", margin: "18px 0 14px" },
  input: {
    width: "100%", padding: "9px 12px", borderRadius: 8,
    border: "1px solid #334155", background: "#0b1220",
    color: "#f1f5f9", fontSize: 13, outline: "none", boxSizing: "border-box",
  },
  foot: {
    padding: "12px 20px", borderTop: "1px solid #1f2a44",
    display: "flex", justifyContent: "space-between", alignItems: "center",
  },
  primary: {
    background: "#10b981", color: "white", border: "none",
    padding: "10px 18px", borderRadius: 8, fontWeight: 700, cursor: "pointer",
  },
  linkBtn: {
    background: "transparent", border: "1px solid #334155",
    color: "#cbd5e1", padding: "10px 14px", borderRadius: 8,
    cursor: "pointer", fontSize: 13,
  },
  smallBtn: {
    background: "transparent", border: "1px solid #334155",
    color: "#cbd5e1", padding: "6px 12px", borderRadius: 6, cursor: "pointer",
    fontSize: 12,
  },
  versionRow: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "10px 14px", border: "1px solid #1f2a44", borderRadius: 8,
    marginBottom: 8, background: "#0b1220",
  },
};
